/**
 * Sort members by age (youngest first) or creation date if age unknown
 * @param {Array} members - Array of member objects
 * @param {Map} memberMap - Map of member ID to member object
 * @returns {Array} Sorted array of member IDs
 */
function sortByAgeOrCreation(memberIds, memberMap) {
  return [...memberIds].sort((aId, bId) => {
    const a = memberMap.get(aId);
    const b = memberMap.get(bId);

    // If both have birth dates, sort by birth date (youngest/most recent first = left)
    if (a.birth_date && b.birth_date) {
      return new Date(b.birth_date) - new Date(a.birth_date);
    }

    // If only one has birth date, put the one without birth date last
    if (a.birth_date && !b.birth_date) return -1;
    if (!a.birth_date && b.birth_date) return 1;

    // Neither has birth date, sort by creation date (earlier first)
    const aCreated = a.created_at ? new Date(a.created_at) : new Date(0);
    const bCreated = b.created_at ? new Date(b.created_at) : new Date(0);
    return aCreated - bCreated;
  });
}

/**
 * Build couple units - groups of spouses/co-parents who should be positioned together
 * @param {Array} members - Array of member objects with relationship data
 * @param {Map} memberMap - Map of member ID to member object
 * @returns {Array} Array of couple unit objects { members: [id1, id2, ...], children: [childId1, ...] }
 */
function buildCoupleUnits(members, memberMap) {
  const units = [];
  const processedPairs = new Set();

  for (const member of members) {
    // Process spouse relationships
    for (const spouseId of member.spouses) {
      const pairKey = [member.id, spouseId].sort().join('-');
      if (processedPairs.has(pairKey)) continue;
      processedPairs.add(pairKey);

      // Find shared children
      const memberChildren = new Set(member.children);
      const spouse = memberMap.get(spouseId);
      const sharedChildren = spouse.children.filter(childId => memberChildren.has(childId));

      units.push({
        members: [member.id, spouseId],
        children: sharedChildren,
        type: 'spouse'
      });
    }

    // Check for co-parents (share children but not marked as spouses)
    const childrenSet = new Set(member.children);
    for (const childId of member.children) {
      const child = memberMap.get(childId);
      if (!child) continue;

      for (const otherParentId of child.parents) {
        if (otherParentId === member.id) continue;

        const pairKey = [member.id, otherParentId].sort().join('-');
        if (processedPairs.has(pairKey)) continue;

        // Check if they're already spouses (would have been processed above)
        if (member.spouses.includes(otherParentId)) continue;

        processedPairs.add(pairKey);

        const otherParent = memberMap.get(otherParentId);
        const sharedChildren = otherParent.children.filter(cId => childrenSet.has(cId));

        units.push({
          members: [member.id, otherParentId],
          children: sharedChildren,
          type: 'co-parent'
        });
      }
    }

    // Process explicit co-parent relationships (even without shared children)
    for (const coparentId of member.coparents) {
      const pairKey = [member.id, coparentId].sort().join('-');
      if (processedPairs.has(pairKey)) continue;

      // Check if they're already spouses
      if (member.spouses.includes(coparentId)) continue;

      processedPairs.add(pairKey);

      // Find shared children (if any)
      const coparent = memberMap.get(coparentId);
      const sharedChildren = coparent.children.filter(cId => childrenSet.has(cId));

      units.push({
        members: [member.id, coparentId],
        children: sharedChildren,
        type: 'co-parent'
      });
    }
  }

  return units;
}

/**
 * Build tree structures from members and relationships
 * Returns an array of tree objects, where each tree represents a connected component
 */
export function buildFamilyTrees(members, relationships) {
  // Create a map of member ID to member object
  const memberMap = new Map();
  members.forEach(member => {
    memberMap.set(member.id, {
      ...member,
      parents: [],
      children: [],
      siblings: [],
      spouses: [],
      coparents: []
    });
  });

  // Process relationships to build parent-child and sibling connections
  relationships.forEach(rel => {
    const person1 = memberMap.get(rel.person1_id);
    const person2 = memberMap.get(rel.person2_id);

    if (!person1 || !person2) return;

    if (rel.relationship_type === 'parent') {
      // person1 is parent of person2
      person1.children.push(person2.id);
      person2.parents.push(person1.id);
    } else if (rel.relationship_type === 'child') {
      // person1 is child of person2
      person2.children.push(person1.id);
      person1.parents.push(person2.id);
    } else if (rel.relationship_type === 'sibling') {
      // Bidirectional sibling relationship
      if (!person1.siblings.includes(person2.id)) {
        person1.siblings.push(person2.id);
      }
      if (!person2.siblings.includes(person1.id)) {
        person2.siblings.push(person1.id);
      }
    } else if (rel.relationship_type === 'spouse') {
      // Bidirectional spouse relationship
      if (!person1.spouses.includes(person2.id)) {
        person1.spouses.push(person2.id);
      }
      if (!person2.spouses.includes(person1.id)) {
        person2.spouses.push(person1.id);
      }
    } else if (rel.relationship_type === 'co-parent') {
      // Bidirectional co-parent relationship
      if (!person1.coparents.includes(person2.id)) {
        person1.coparents.push(person2.id);
      }
      if (!person2.coparents.includes(person1.id)) {
        person2.coparents.push(person1.id);
      }
    }
  });

  // Find all connected components (separate trees)
  const visited = new Set();
  const trees = [];

  function findConnectedMembers(memberId, connectedIds = new Set()) {
    if (visited.has(memberId) || connectedIds.has(memberId)) return;

    connectedIds.add(memberId);
    visited.add(memberId);

    const member = memberMap.get(memberId);
    if (!member) return;

    // Traverse all connections (including spouses and co-parents)
    [...member.parents, ...member.children, ...member.siblings, ...member.spouses, ...member.coparents].forEach(relatedId => {
      findConnectedMembers(relatedId, connectedIds);
    });

    return connectedIds;
  }

  // Build separate trees for each connected component
  members.forEach(member => {
    if (!visited.has(member.id)) {
      const connectedIds = findConnectedMembers(member.id);
      if (connectedIds && connectedIds.size > 0) {
        const treeMembers = Array.from(connectedIds).map(id => memberMap.get(id));
        trees.push({
          members: treeMembers,
          rootId: findTreeRoot(treeMembers)
        });
      } else {
        // Member with no connections - create a single-node tree
        trees.push({
          members: [memberMap.get(member.id)],
          rootId: member.id
        });
      }
    }
  });

  return trees;
}

/**
 * Find the root member (someone with no parents) in a tree
 * If everyone has parents (circular), return the first member
 */
function findTreeRoot(members) {
  // Find someone with no parents
  const memberWithNoParents = members.find(m => m.parents.length === 0);
  if (memberWithNoParents) return memberWithNoParents.id;

  // If everyone has parents, just return the first one
  return members[0]?.id;
}

/**
 * Calculate hierarchical positions for tree layout
 * Builds outward from focal person:
 * - Focal person at center
 * - Parents positioned above, inheriting left/right bias
 * - Children positioned below, centered between parents
 * - Grandparents inherit position bias from their child's couple position
 * - Single children are centered exactly between their parents
 * - Multiple children are evenly distributed
 * - Collision detection ensures minimum spacing between nodes
 */
export function calculateTreeLayout(tree, degreeMap = new Map(), focalPersonId = null) {
  const positions = new Map();
  const { members, rootId } = tree;

  // Layout Constants
  const NODE_WIDTH = 150;    // Horizontal spacing unit (not actual node width)
  const NODE_HEIGHT = 200;   // Vertical spacing between generations
  const SPOUSE_GAP = 80;     // Horizontal gap between spouses/co-parents

  // Handle single-member trees (no relationships)
  if (members.length === 1) {
    positions.set(members[0].id, { x: 0, y: 0 });
    return positions;
  }

  const memberMap = new Map(members.map(m => [m.id, m]));

  // Determine starting point - use focal person if in tree, otherwise rootId
  const startId = (focalPersonId && memberMap.has(focalPersonId)) ? focalPersonId : rootId;

  // ============================================
  // PHASE 1: Assign Y levels (generations) from focal person
  // ============================================
  const levels = new Map();
  const visited = new Set();

  function assignLevels(memberId, level = 0) {
    if (visited.has(memberId)) return;
    visited.add(memberId);

    levels.set(memberId, level);
    const member = memberMap.get(memberId);

    if (member) {
      // Children go to next level down
      member.children.forEach(childId => {
        if (!visited.has(childId)) {
          assignLevels(childId, level + 1);
        }
      });

      // Parents go to level up
      member.parents.forEach(parentId => {
        if (!visited.has(parentId)) {
          assignLevels(parentId, level - 1);
        }
      });

      // Siblings stay on same level
      member.siblings.forEach(siblingId => {
        if (!visited.has(siblingId)) {
          assignLevels(siblingId, level);
        }
      });

      // Spouses stay on same level
      member.spouses.forEach(spouseId => {
        if (!visited.has(spouseId)) {
          assignLevels(spouseId, level);
        }
      });

      // Co-parents stay on same level
      member.coparents.forEach(coparentId => {
        if (!visited.has(coparentId)) {
          assignLevels(coparentId, level);
        }
      });
    }
  }

  assignLevels(startId);

  // Normalize levels (focal person's level becomes 0)
  const focalLevel = levels.get(startId) || 0;
  levels.forEach((level, memberId) => {
    levels.set(memberId, level - focalLevel);
  });

  // Group members by level
  const levelGroups = new Map();
  levels.forEach((level, memberId) => {
    if (!levelGroups.has(level)) {
      levelGroups.set(level, []);
    }
    levelGroups.get(level).push(memberId);
  });

  // ============================================
  // PHASE 2: Build couple units
  // ============================================
  const coupleUnits = buildCoupleUnits(members, memberMap);

  // Track which members are part of a couple unit
  const memberToUnit = new Map();

  // First pass: assign spouse units (higher priority)
  coupleUnits.forEach((unit, index) => {
    if (unit.type === 'spouse') {
      unit.members.forEach(memberId => {
        memberToUnit.set(memberId, index);
      });
    }
  });

  // Second pass: assign co-parent units only if member isn't already in a spouse unit
  coupleUnits.forEach((unit, index) => {
    if (unit.type === 'co-parent') {
      unit.members.forEach(memberId => {
        if (!memberToUnit.has(memberId)) {
          memberToUnit.set(memberId, index);
        }
      });
    }
  });

  // ============================================
  // PHASE 3: Position outward from focal person
  // ============================================
  const positioned = new Set();

  // Track horizontal bias for ancestor positioning
  // bias > 0 means "position to the right", bias < 0 means "position to the left"
  const horizontalBias = new Map();

  // Position focal person at center
  const focalY = levels.get(startId) * NODE_HEIGHT;

  // Check if focal person is part of a couple
  const focalUnitIndex = memberToUnit.get(startId);
  if (focalUnitIndex !== undefined) {
    const unit = coupleUnits[focalUnitIndex];
    const sortedUnitMembers = sortUnitMembers(unit.members, memberMap);
    const unitWidth = (sortedUnitMembers.length - 1) * SPOUSE_GAP;
    const unitStartX = -unitWidth / 2;

    sortedUnitMembers.forEach((umId, idx) => {
      const x = unitStartX + idx * SPOUSE_GAP;
      positions.set(umId, { x, y: focalY });
      positioned.add(umId);
      // Set bias based on position relative to center
      horizontalBias.set(umId, x);
    });
  } else {
    positions.set(startId, { x: 0, y: focalY });
    positioned.add(startId);
    horizontalBias.set(startId, 0);
  }

  // BFS outward from focal person, processing by relationship distance
  const queue = [startId];
  const processedForBFS = new Set([startId]);

  // Also add focal person's spouse to start queue if present
  if (focalUnitIndex !== undefined) {
    coupleUnits[focalUnitIndex].members.forEach(id => {
      if (!processedForBFS.has(id)) {
        queue.push(id);
        processedForBFS.add(id);
      }
    });
  }

  while (queue.length > 0) {
    const currentId = queue.shift();
    const current = memberMap.get(currentId);
    const currentPos = positions.get(currentId);
    const currentLevel = levels.get(currentId);
    const currentBias = horizontalBias.get(currentId) || 0;

    if (!current) continue;

    // FIRST: Process unpositioned spouse/co-parent (must be adjacent)
    const unpositionedPartners = [...current.spouses, ...current.coparents].filter(pid => !positioned.has(pid));
    for (const partnerId of unpositionedPartners) {
      if (positioned.has(partnerId)) continue;

      const partner = memberMap.get(partnerId);
      const partnerY = currentLevel * NODE_HEIGHT;

      // Position partner next to current (male left, female right)
      let xOffset = SPOUSE_GAP;
      if (partner && partner.gender && current.gender) {
        if (partner.gender === 'male' && current.gender === 'female') {
          xOffset = -SPOUSE_GAP;
        }
      }

      positions.set(partnerId, { x: currentPos.x + xOffset, y: partnerY });
      positioned.add(partnerId);
      // Carry bias plus horizontal offset so the partner's ancestors stay on their side
      horizontalBias.set(partnerId, currentBias + xOffset);
      if (!processedForBFS.has(partnerId)) {
        queue.push(partnerId);
        processedForBFS.add(partnerId);
      }
    }

    // SECOND: Process parents (go up)
    const unpositionedParents = current.parents.filter(pid => !positioned.has(pid));
    if (unpositionedParents.length > 0) {
      const parentY = (currentLevel - 1) * NODE_HEIGHT;

      // Sort parents: male left, female right
      const sortedParents = sortUnitMembers(unpositionedParents, memberMap);

      // Check if parents are a couple (married/co-parents)
      const parentUnit = findUnitContainingAll(sortedParents, coupleUnits, memberToUnit);

      if (parentUnit) {
        // Parents are a couple - position them together, centered above current
        // But inherit the horizontal bias from current member
        const allParentsInUnit = parentUnit.members.filter(pid => memberMap.has(pid));
        const sortedAllParents = sortUnitMembers(allParentsInUnit, memberMap);
        const unitWidth = (sortedAllParents.length - 1) * SPOUSE_GAP;
        const unitCenterX = currentPos.x + currentBias * 0.5; // Inherit bias
        const unitStartX = unitCenterX - unitWidth / 2;

        sortedAllParents.forEach((pid, idx) => {
          if (!positioned.has(pid)) {
            const x = unitStartX + idx * SPOUSE_GAP;
            positions.set(pid, { x, y: parentY });
            positioned.add(pid);
            // Parents inherit and amplify the bias
            horizontalBias.set(pid, currentBias + (x - currentPos.x));
            if (!processedForBFS.has(pid)) {
              queue.push(pid);
              processedForBFS.add(pid);
            }
          }
        });
      } else {
        // Parents not coupled - position each above, with bias
        const parentWidth = (sortedParents.length - 1) * SPOUSE_GAP;
        const parentStartX = currentPos.x - parentWidth / 2;

        sortedParents.forEach((pid, idx) => {
          const x = parentStartX + idx * SPOUSE_GAP;
          positions.set(pid, { x, y: parentY });
          positioned.add(pid);
          horizontalBias.set(pid, currentBias);
          if (!processedForBFS.has(pid)) {
            queue.push(pid);
            processedForBFS.add(pid);
          }
        });
      }
    }

    // THIRD: Process children (go down) - centered between parents
    if (current.children.length > 0) {
      const childY = (currentLevel + 1) * NODE_HEIGHT;

      // Find co-parent to center children between parents
      let parentsCenterX = currentPos.x;
      const coParentIds = [...current.spouses, ...current.coparents];
      for (const cpId of coParentIds) {
        if (positioned.has(cpId)) {
          const cpPos = positions.get(cpId);
          parentsCenterX = (currentPos.x + cpPos.x) / 2;
          break;
        }
      }

      // Sort all children by age so positioned and unpositioned kids share one order
      const sortedChildren = sortByAgeOrCreation(current.children, memberMap);

      // Build child units - each child or child+spouse is a unit
      const childUnits = [];
      const processedChildren = new Set();

      for (const childId of sortedChildren) {
        if (processedChildren.has(childId)) continue;

        const unitIdx = memberToUnit.get(childId);
        if (unitIdx !== undefined) {
          const unit = coupleUnits[unitIdx];
          const unitMembers = unit.members.filter(m => memberMap.has(m));
          const sortedUnitMembers = sortUnitMembers(unitMembers, memberMap);
          childUnits.push(sortedUnitMembers);
          unit.members.forEach(m => processedChildren.add(m));
        } else {
          childUnits.push([childId]);
          processedChildren.add(childId);
        }
      }

      // Width bookkeeping for each unit
      const unitWidths = childUnits.map(members => (members.length - 1) * SPOUSE_GAP);
      let totalWidth = unitWidths.reduce((sum, w) => sum + w, 0);
      if (childUnits.length > 1) {
        totalWidth += NODE_WIDTH * (childUnits.length - 1);
      }

      // Pre-compute offsets for each unit (distance from the first unit start)
      const unitOffsets = [];
      let offset = 0;
      childUnits.forEach((unit, idx) => {
        unitOffsets.push(offset);
        offset += unitWidths[idx] + (idx < childUnits.length - 1 ? NODE_WIDTH : 0);
      });

      // If some children are already positioned (e.g., focal person), align layout to them
      const anchoredStarts = [];
      const anchoredBaseByUnit = new Map();

      childUnits.forEach((unit, idx) => {
        const baseCandidates = unit
          .map((memberId, memberIdx) => {
            if (!positioned.has(memberId)) return null;
            const pos = positions.get(memberId);
            if (!pos) return null;
            return pos.x - (memberIdx * SPOUSE_GAP);
          })
          .filter(v => v !== null);

        if (baseCandidates.length > 0) {
          const baseX = baseCandidates.reduce((a, b) => a + b, 0) / baseCandidates.length;
          anchoredBaseByUnit.set(idx, baseX);
          anchoredStarts.push(baseX - unitOffsets[idx]);
        }
      });

      let startX = parentsCenterX - (totalWidth / 2);
      if (anchoredStarts.length > 0) {
        startX = anchoredStarts.reduce((a, b) => a + b, 0) / anchoredStarts.length;
      }

      const hasUnpositioned = childUnits.some(unit => unit.some(id => !positioned.has(id)));
      if (hasUnpositioned) {
        childUnits.forEach((unit, idx) => {
          const baseFromAnchor = anchoredBaseByUnit.get(idx);
          const unitBaseX = baseFromAnchor !== undefined ? baseFromAnchor : startX + unitOffsets[idx];

          unit.forEach((memberId, memberIdx) => {
            if (!positioned.has(memberId)) {
              const x = unitBaseX + memberIdx * SPOUSE_GAP;
              positions.set(memberId, { x, y: childY });
              positioned.add(memberId);
              // Carry horizontal bias relative to the parents' center so their ancestors stay on that side
              horizontalBias.set(memberId, x - parentsCenterX);
              if (!processedForBFS.has(memberId)) {
                queue.push(memberId);
                processedForBFS.add(memberId);
              }
            }
          });
        });
      }
    }

    // FOURTH: Process siblings (positioned after spouses)
    const unpositionedSiblings = current.siblings.filter(sid => !positioned.has(sid));
    if (unpositionedSiblings.length > 0) {
      const siblingY = currentLevel * NODE_HEIGHT;

      // Find rightmost position of current and their spouse(s)
      let rightmostX = currentPos.x;
      const partnerIds = [...current.spouses, ...current.coparents];
      for (const partnerId of partnerIds) {
        if (positioned.has(partnerId)) {
          const partnerPos = positions.get(partnerId);
          rightmostX = Math.max(rightmostX, partnerPos.x);
        }
      }

      // Position siblings starting from rightmost position + gap
      let siblingX = rightmostX + NODE_WIDTH;

      for (const sibId of unpositionedSiblings) {
        if (positioned.has(sibId)) continue;

        const unitIdx = memberToUnit.get(sibId);
        if (unitIdx !== undefined) {
          const unit = coupleUnits[unitIdx];
          const unposInUnit = unit.members.filter(m => !positioned.has(m));
          const sortedUnitMembers = sortUnitMembers(unposInUnit, memberMap);

          sortedUnitMembers.forEach((umId, idx) => {
            if (!positioned.has(umId)) {
              positions.set(umId, { x: siblingX + idx * SPOUSE_GAP, y: siblingY });
              positioned.add(umId);
              horizontalBias.set(umId, currentBias);
              if (!processedForBFS.has(umId)) {
                queue.push(umId);
                processedForBFS.add(umId);
              }
            }
          });
          siblingX += (sortedUnitMembers.length - 1) * SPOUSE_GAP + NODE_WIDTH;
        } else {
          positions.set(sibId, { x: siblingX, y: siblingY });
          positioned.add(sibId);
          horizontalBias.set(sibId, currentBias);
          siblingX += NODE_WIDTH;
          if (!processedForBFS.has(sibId)) {
            queue.push(sibId);
            processedForBFS.add(sibId);
          }
        }
      }
    }
  }

  // ============================================
  // PHASE 4: Position any remaining unpositioned members
  // ============================================
  const sortedLevels = Array.from(levelGroups.keys()).sort((a, b) => a - b);

  for (const level of sortedLevels) {
    const memberIds = levelGroups.get(level);
    const y = level * NODE_HEIGHT;

    // Find max X on this level for placing unpositioned members
    let maxX = -Infinity;
    for (const mid of memberIds) {
      if (positioned.has(mid)) {
        maxX = Math.max(maxX, positions.get(mid).x);
      }
    }
    if (maxX === -Infinity) maxX = -NODE_WIDTH;

    for (const memberId of memberIds) {
      if (positioned.has(memberId)) continue;

      maxX += NODE_WIDTH;
      positions.set(memberId, { x: maxX, y });
      positioned.add(memberId);
    }
  }

  // ============================================
  // PHASE 5: Collision detection and resolution
  // ============================================
  // Minimum spacing between node centers (accounts for node radius + small gap)
  const MIN_NODE_SPACING = NODE_WIDTH * 0.7; // 105px - gives about 25px gap between node edges

  for (const level of sortedLevels) {
    const memberIds = levelGroups.get(level);

    // Get positions for this level, sorted by X
    const levelPositions = memberIds
      .map(id => ({ id, ...positions.get(id) }))
      .sort((a, b) => a.x - b.x);

    // Check for collisions and shift right
    for (let i = 1; i < levelPositions.length; i++) {
      const prev = levelPositions[i - 1];
      const curr = levelPositions[i];

      const actualGap = curr.x - prev.x;

      // Check if they're in a couple unit together (spouses should be closer)
      const prevMember = memberMap.get(prev.id);
      const areSpouses = prevMember?.spouses.includes(curr.id) || 
                        prevMember?.coparents.includes(curr.id);

      // Use different minimum gaps for spouses vs non-spouses
      const minGap = areSpouses ? SPOUSE_GAP * 0.9 : MIN_NODE_SPACING;

      if (actualGap < minGap) {
        const shift = minGap - actualGap;
        // Shift all nodes from current position onwards
        for (let j = i; j < levelPositions.length; j++) {
          const pos = positions.get(levelPositions[j].id);
          positions.set(levelPositions[j].id, { x: pos.x + shift, y: pos.y });
          levelPositions[j].x += shift;
        }
      }
    }
  }

  // ============================================
  // PHASE 6: Center the tree on focal person
  // ============================================
  const focalPos = positions.get(startId);
  if (focalPos) {
    const centerOffset = focalPos.x;
    positions.forEach((pos, id) => {
      positions.set(id, { x: pos.x - centerOffset, y: pos.y });
    });
  }

  return positions;
}

/**
 * Find a couple unit that contains all the given member IDs
 */
function findUnitContainingAll(memberIds, coupleUnits, memberToUnit) {
  if (memberIds.length === 0) return null;

  const firstUnitIndex = memberToUnit.get(memberIds[0]);
  if (firstUnitIndex === undefined) return null;

  const unit = coupleUnits[firstUnitIndex];
  // Check if all memberIds are in this unit
  const allInUnit = memberIds.every(id => unit.members.includes(id));

  return allInUnit ? unit : null;
}

/**
 * Sort unit members for horizontal positioning
 * Male left, female right; if gender unknown, by creation date
 */
function sortUnitMembers(memberIds, memberMap) {
  return [...memberIds].sort((aId, bId) => {
    const a = memberMap.get(aId);
    const b = memberMap.get(bId);

    // If both have gender, male goes left
    if (a.gender && b.gender) {
      if (a.gender === 'male' && b.gender !== 'male') return -1;
      if (a.gender !== 'male' && b.gender === 'male') return 1;
    }

    // Otherwise sort by creation date (earlier left)
    const aCreated = a.created_at ? new Date(a.created_at) : new Date(0);
    const bCreated = b.created_at ? new Date(b.created_at) : new Date(0);
    return aCreated - bCreated;
  });
}

/**
 * Group members into sibling groups (members who share at least one parent)
 */
function groupSiblings(memberIds, memberMap) {
  const groups = [];
  const processed = new Set();

  for (const memberId of memberIds) {
    if (processed.has(memberId)) continue;

    const member = memberMap.get(memberId);
    const group = [memberId];
    processed.add(memberId);

    // Find all siblings in this list
    for (const otherId of memberIds) {
      if (processed.has(otherId)) continue;

      const other = memberMap.get(otherId);

      // Check if they share a parent
      const shareParent = member.parents.some(pid => other.parents.includes(pid));
      // Or if they're marked as siblings
      const areSiblings = member.siblings.includes(otherId);

      if (shareParent || areSiblings) {
        group.push(otherId);
        processed.add(otherId);
      }
    }

    groups.push(group);
  }

  return groups;
}
